import MenuList from '@components/common/menu/MenuList';
import { buildContextMenuFromType } from '@components/map/contextMenu/builderFactory';
import {
  HasDisengageContext,
  HasEngageContext,
  HasEventContext,
  HasMeasureContext,
  HasPointCUASContext,
  HasQrCodeContext,
} from '@components/map/contextMenu/builders/common';
import { TargetContextMenuContext, TargetContextMenuContextCore } from '@components/map/contextMenu/builders/target';
import { FeatureTypeToContextComponentEnum } from '@components/map/contextMenu/type';
import { useMapContext } from '@components/map/MapContext';
import { createToast, ToastStatusEnum } from '@components/ui/toaster';
import { useAppSelector } from '@hooks/redux.hooks';
import { useCameraCommand } from '@hooks/useCameraCommand';
import { MeasureOriginEnum, useMeasureFromPointMenuItem } from '@hooks/useMeasureFromPointMenuItem';
import { usePrevious } from '@hooks/usePrevious';
import { useSelectorWithMapId } from '@hooks/useSelectorWithMapId';
import { useSelectorWithReplayMode } from '@hooks/useSelectorWithReplayMode';
import { useSkyJackerCommand } from '@hooks/useSkyJackerCommand';
import { useWithDispatch } from '@hooks/useWithDispatch';
import {
  hasRole,
  hasSensorRoleFromActiveProfile,
  selectActiveOrganization,
  selectActiveUser,
  selectActiveUserLogin,
} from '@redux/authent/authent.selectors';
import { selectActiveGlobalScreenConfiguration, selectAllOrganizations } from '@redux/config/config.selectors';
import { updateSelectedCameraKey } from '@redux/global/global.reducer';
import { closePopupByMapIdAndType, updatePopupByMapId } from '@redux/maps/maps.reducer';
import { selectPopupsByMapId } from '@redux/maps/maps.selectors';
import { selectContext } from '@redux/settings/settings.selectors';
import {
  selectClosestActiveMovableCamera,
  selectHealthySkyJackerActiveMonitoring,
  selectIsNeutralSensorJamming,
  selectMovableCameraActiveMonitoringSensors,
  selectTrackingStatesOfMovableCameras,
} from '@redux/situation/monitoring.selectors';
import { selectNeutralInfo, selectSituationTime } from '@redux/situation/situation.selectors';
import { RootState } from '@redux/store';
import { usePrepareEngagedTargetMutation } from '@services/c2/engagement.api';
import { useUpdatePlatformOffsetMutation } from '@services/config/platform.api';
import { usePatchContextMutation } from '@services/config/setting.api';
import { createErrorMessage, getOrganizationsCodesFromMarks } from '@utils/common.utils';
import { exportObjectInJson } from '@utils/importExport.utils';
import { findPopupByType, isOpenedPopupByType } from '@utils/popup.utils';
import { isTargetEngaged } from '@utils/sensors/neutral/neutral.utils';
import { getSensorUniqueKey } from '@utils/sensors/sensors.utils';
import { getTargetHeight, getTargetIdentification } from '@utils/target.utils';
import { toLngLat } from '@utils/validation/coordinates.utils';
import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useMap } from 'react-map-gl/mapbox';

import { RoleEnum } from '@/types/authent/roles.types';
import { CameraObjectSizeEnum, Target } from '@/types/c2/c2.types';
import { ActionEnum, Mark, MarkEnum } from '@/types/commons/commons.types';
import { ContextEnum } from '@/types/config/screenConfiguration.types';
import { NotificationTypeEnum } from '@/types/data/data.types';
import {
  EventPopupData,
  InfoDataTypeEnum,
  PopupDataType,
  PopupTypeEnum,
  ShareHoloviewPopupData,
} from '@/types/popup.types';
import { CameraConfiguration } from '@/types/sensor/configuration.types';
import { IdentificationEnum } from '@/types/sensor/identification.types';
import { ActiveMonitoringSensorByType } from '@/types/sensor/monitoring.types';
import { LocationTypeEnum, SensorFamilyEnum, TargetLocation } from '@/types/sensor/sensor.types';
import { SensorTypeEnum, SpecificStateEnum, TrackingStatusEnum, TrackingTypeEnum } from '@/types/sensor/sensors';
import { CameraStatus } from '@/types/sensor/status.types';

interface TargetContextMenuProps {
  clickPosition: { x: number; y: number };
  selectedTarget: Target<TargetLocation>;
  onClose: () => void;
}

function includes(marks: Mark[], markType: MarkEnum): boolean {
  return marks.some((m) => m.type === markType);
}

export function TargetContextMenu({ selectedTarget, clickPosition, onClose }: Readonly<TargetContextMenuProps>) {
  const { current: map } = useMap();
  const { mapId } = useMapContext();
  const closePopupByType = useWithDispatch(closePopupByMapIdAndType);

  const { formatMessage } = useIntl();
  const situationTime = useAppSelector(selectSituationTime);
  const popups = useSelectorWithMapId(selectPopupsByMapId);
  const updatePopup = useWithDispatch(updatePopupByMapId);
  const updateGlobalSelectedCameraKey = useWithDispatch(updateSelectedCameraKey);
  const camSensorAction = { action: ActionEnum.COMMAND, sensorFamily: SensorFamilyEnum.CAMERA };
  const accessCamCommand = useAppSelector((state) => hasSensorRoleFromActiveProfile(state, camSensorAction));
  const accessEventCommand = useAppSelector((state) => hasRole(state, RoleEnum.CREATE_REPLAY));
  const neutralSensorAction = { action: ActionEnum.COMMAND, sensorFamily: SensorFamilyEnum.NEUTRAL };
  const accessNeutralCommand = useAppSelector((state) => hasSensorRoleFromActiveProfile(state, neutralSensorAction));
  const lastLocation = selectedTarget.lastPosition.location;
  const canManageClearance = useAppSelector((state: RootState) => hasRole(state, RoleEnum.MANAGE_CLEARANCE));
  const canMarkTracks = useAppSelector((state: RootState) => hasRole(state, RoleEnum.MARK_TRACKS));
  const isAdmin = useAppSelector((state: RootState) => hasRole(state, RoleEnum.ADMIN));
  const neutralInfo = useSelectorWithReplayMode(selectNeutralInfo);
  const targetEngaged = isTargetEngaged(neutralInfo, selectedTarget.displayId ?? selectedTarget.id);
  const isNeutralSensorJamming = useSelectorWithReplayMode(selectIsNeutralSensorJamming);
  const activeUserLogin = useAppSelector(selectActiveUserLogin);
  const isNonActiveUserJamming =
    isNeutralSensorJamming && !!neutralInfo?.login && activeUserLogin !== neutralInfo.login;
  const user = useAppSelector(selectActiveUser);
  const currentContext = useAppSelector(selectContext);

  const movableLadCameras = useAppSelector(selectMovableCameraActiveMonitoringSensors);
  const closestMovableCameraLAD = useSelectorWithReplayMode(
    selectClosestActiveMovableCamera,
    selectedTarget.lastPosition.location?.position ?? null,
  );

  const healthySkyJackerSensors = useAppSelector(selectHealthySkyJackerActiveMonitoring);

  const trackingStateOfMovableCameras = useAppSelector(selectTrackingStatesOfMovableCameras);

  const useCamCommand = useCameraCommand({
    monitoringSensor: null,
  });

  const { setEmission, sensorTracking } = useSkyJackerCommand(null);

  const activeGlobalScreenConfiguration = useAppSelector(selectActiveGlobalScreenConfiguration);
  const ladContextIsPresent = !!activeGlobalScreenConfiguration?.screenConfigurations.some(
    (screenConfig) => screenConfig.context === ContextEnum.DOUBT_LAD && screenConfig.gridLayout,
  );

  const position = selectedTarget.lastPosition.location?.position;

  const [patchContext] = usePatchContextMutation();

  const targetInfo = findPopupByType(popups, PopupTypeEnum.INFO, InfoDataTypeEnum.INFO_TARGET);
  const droneHistoryPopup = findPopupByType(popups, PopupTypeEnum.HISTORY, NotificationTypeEnum.DRONE);

  const [updateOffset] = useUpdatePlatformOffsetMutation();
  const organizations = useAppSelector(selectAllOrganizations);
  const organization = useAppSelector(selectActiveOrganization);

  const [prepareEngagedTarget] = usePrepareEngagedTargetMutation();
  const [isLoadingPrepareTarget, setIsLoadingPrepareTarget] = useState(false);
  const prevIsTargetEngaged = usePrevious(targetEngaged, targetEngaged);

  const openPopup = useCallback(
    (popupDataType: PopupDataType) => {
      updatePopup({
        mapId,
        popup: {
          ...popupDataType,
          position: { x: clickPosition.x, y: clickPosition.y },
        },
      });
    },
    [clickPosition.x, clickPosition.y, mapId, updatePopup],
  );

  function handleOpenTargetInfo() {
    openPopup({
      type: PopupTypeEnum.INFO,
      data: {
        type: InfoDataTypeEnum.INFO_TARGET,
        targetId: selectedTarget.id,
        openingDate: new Date(),
      },
    });

    onClose();
  }

  function handleOpenHistoryPopup() {
    if (getTargetIdentification(selectedTarget, organization) === IdentificationEnum.DRONE) {
      openPopup({
        type: PopupTypeEnum.HISTORY,
        data: {
          identifiers: [selectedTarget.id],
          displayId: selectedTarget.displayId,
          type: NotificationTypeEnum.DRONE,
          openingDate: situationTime ? new Date(situationTime) : new Date(),
        },
      });
      onClose();
    }
  }

  function startTracking(cameraConfiguration: CameraConfiguration) {
    useCamCommand.sensorTracking(selectedTarget, true, cameraConfiguration);
    updateGlobalSelectedCameraKey(cameraConfiguration.uniqueKey);
  }

  function startNeutralTracking(monitoringSensor: ActiveMonitoringSensorByType<SensorTypeEnum.SKY_JACKER>) {
    sensorTracking(selectedTarget, monitoringSensor.configuration);
    setEmission(true, monitoringSensor.configuration);
  }

  function handleTracking(cameraConfiguration: CameraConfiguration, cameraStatus: CameraStatus) {
    const cameraUniqueKey = getSensorUniqueKey(cameraStatus);
    const isGPSTracking =
      cameraStatus.trackingStatus.globalTrackingType === TrackingTypeEnum.SENSOR &&
      cameraStatus.trackingStatus.sensorTrackingStatus.globalSensorTrackingState === TrackingStatusEnum.ACTIVE;
    const isGPSTrackingSelectedTarget =
      isGPSTracking && cameraStatus.trackingStatus.sensorTrackingStatus.currentTrackedObjectId === selectedTarget.id;
    const isVideoTracking = trackingStateOfMovableCameras.some(
      (value) =>
        value.uniqueKey === cameraUniqueKey &&
        value.status.globalTrackingType === TrackingTypeEnum.VIDEO &&
        value.status.videoTrackingStatus.globalVideoTrackingState === TrackingStatusEnum.ACTIVE,
    );
    const hasRoundOnGoing = cameraStatus.specificState === SpecificStateEnum.ROUND;

    if (isGPSTrackingSelectedTarget) {
      useCamCommand.sensorTracking(null, true, cameraConfiguration);
      useCamCommand.releaseToken(cameraConfiguration);
      updateGlobalSelectedCameraKey(null);
    } else if (isGPSTracking || isVideoTracking || hasRoundOnGoing) {
      updatePopup({
        mapId: mapId,
        popup: {
          type: PopupTypeEnum.CONFIRMATION_POPUP,
          position: { x: clickPosition.x, y: clickPosition.y },
          data: {
            title: formatMessage({ id: 'contextmenu.actions.target.camera.confirmationTitle' }),
            description: formatMessage(
              { id: 'contextmenu.actions.target.camera.confirmationDescription' },
              { isTracking: isGPSTracking || isVideoTracking },
            ),
            submitLabel: formatMessage({ id: 'contextmenu.actions.target.camera.confirmationSubmit' }),
            onSubmit: () => {
              startTracking(cameraConfiguration);
              closePopupByType({ mapId, type: PopupTypeEnum.CONFIRMATION_POPUP });
            },
          },
        },
      });
    } else {
      startTracking(cameraConfiguration);
    }
    onClose();
  }

  function handleNeutralization(monitoringSensor: ActiveMonitoringSensorByType<SensorTypeEnum.SKY_JACKER>) {
    const isGPSTracking =
      monitoringSensor.status.trackingStatus.globalSensorTrackingState === TrackingStatusEnum.ACTIVE;
    const isGPSTrackingSelectedTarget =
      isGPSTracking && monitoringSensor.status.trackingStatus.currentTrackedObjectId === selectedTarget.id;

    if (isGPSTrackingSelectedTarget) {
      sensorTracking(null, monitoringSensor.configuration);
    } else if (isGPSTracking) {
      updatePopup({
        mapId: mapId,
        popup: {
          type: PopupTypeEnum.CONFIRMATION_POPUP,
          position: { x: clickPosition.x, y: clickPosition.y },
          data: {
            title: formatMessage({ id: 'contextmenu.actions.target.neutral.confirmationTitle' }),
            description: formatMessage(
              { id: 'contextmenu.actions.target.neutral.confirmationDescription' },
              { isTracking: isGPSTracking },
            ),
            submitLabel: formatMessage({
              id: 'contextmenu.actions.target.neutral.confirmationSubmit',
            }),
            onSubmit: () => {
              startNeutralTracking(monitoringSensor);
              closePopupByType({ mapId, type: PopupTypeEnum.CONFIRMATION_POPUP });
            },
          },
        },
      });
    } else {
      startNeutralTracking(monitoringSensor);
    }
    onClose();
  }

  function centerMap() {
    if (lastLocation?.position) {
      map?.getMap().flyTo({ center: [lastLocation.position.longitude, lastLocation.position.latitude], zoom: 14 });
    }
  }

  function handleLAD() {
    if (currentContext !== ContextEnum.DOUBT_LAD) {
      patchContext({ login: user.login, context: ContextEnum.DOUBT_LAD });
    }
    centerMap();
    handleOpenTargetInfo();
    if (closestMovableCameraLAD?.status) {
      startTracking(closestMovableCameraLAD.configuration);
    }
  }

  const handleLookAt = (cameraConfiguration: CameraConfiguration) => {
    if (lastLocation && lastLocation.position) {
      const altitude = lastLocation.type === LocationTypeEnum.SPATIAL ? lastLocation.altitude : null;
      useCamCommand.lookAt(
        { latitude: lastLocation.position.latitude, longitude: lastLocation.position.longitude, type: '2D' },
        null,
        null,
        altitude,
        CameraObjectSizeEnum.TARGET,
        true,
        cameraConfiguration,
      );
      updateGlobalSelectedCameraKey(getSensorUniqueKey(cameraConfiguration));
      onClose();
    }
  };

  function handleShareTargetHoloview() {
    openPopup({
      type: PopupTypeEnum.SHARE_HOLOVIEW,
      data: {
        site: selectedTarget.site,
        trackIds: selectedTarget.trackIds,
      } as ShareHoloviewPopupData,
    });
    onClose();
  }

  function handleUpdateOffset() {
    const marks = getOrganizationsCodesFromMarks(selectedTarget.marks);
    if (marks.length === 1) {
      const organization = organizations.find((o) => o.code === marks[0]);
      if (organization && organization.platforms.length === 1) {
        updateOffset({
          id: organization.platforms[0].id,
          offset: getTargetHeight(selectedTarget) ?? 0,
        })
          .unwrap()
          .catch((error) => createToast(createErrorMessage(error), ToastStatusEnum.ERROR));
      } else {
        createToast(formatMessage({ id: 'error.organizations' }), ToastStatusEnum.ERROR);
      }
    } else {
      createToast(formatMessage({ id: 'error.platforms' }), ToastStatusEnum.ERROR);
    }
    onClose();
  }

  function openGpsQrCodePopup() {
    if (selectedTarget.lastPosition.location?.position) {
      openPopup({
        type: PopupTypeEnum.GPS_QR_CODE,
        data: {
          coordinates: selectedTarget.lastPosition.location.position,
          targetId: selectedTarget.id,
        },
      });
      onClose();
    }
  }

  const handleCreateEventClick = useCallback(() => {
    updatePopup({
      mapId,
      popup: {
        type: PopupTypeEnum.EVENT,
        position: { x: clickPosition.x, y: clickPosition.y },
        data: {
          newEvent: {
            coordinates: position,
            date: new Date().toISOString(),
          },
        } as EventPopupData,
      },
    });
    onClose();
  }, [clickPosition.x, clickPosition.y, mapId, onClose, position, updatePopup]);

  function handleOpenTargetControlPopup() {
    openPopup({
      type: PopupTypeEnum.DRONE_CONTROL,
      data: { targetId: selectedTarget.id, openingDate: new Date() },
    });

    onClose();
  }

  function handleEngagement() {
    if (!accessNeutralCommand || isLoadingPrepareTarget || isNonActiveUserJamming) {
      return;
    }
    setIsLoadingPrepareTarget(true);
    prepareEngagedTarget(selectedTarget.displayId ?? selectedTarget.id);
  }

  function handleDisengagement() {
    if (
      !accessNeutralCommand ||
      isLoadingPrepareTarget ||
      !targetEngaged ||
      isNeutralSensorJamming ||
      isNonActiveUserJamming
    ) {
      return;
    }
    setIsLoadingPrepareTarget(true);
    prepareEngagedTarget(null);
  }

  useEffect(() => {
    if (prevIsTargetEngaged !== targetEngaged) {
      setIsLoadingPrepareTarget(false);
      if (targetEngaged) {
        openPopup({
          type: PopupTypeEnum.ENGAGEMENT,
          data: {
            openingDate: situationTime ? new Date(situationTime) : new Date(),
          },
        });
      }
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose, openPopup, prevIsTargetEngaged, targetEngaged]);

  function handleJsonExport() {
    exportObjectInJson(selectedTarget, selectedTarget.displayId ?? selectedTarget.id);
    onClose();
  }

  const [label, enableMeasure] = useMeasureFromPointMenuItem({
    closeContextMenu: onClose,
    position: position ?? null,
    measureOrigin: MeasureOriginEnum.POINT,
  });

  const qrCodeContext: HasQrCodeContext = {
    position: toLngLat(position),
    openGpsQrCodePopup,
  };
  const measureContext: HasMeasureContext = {
    position: toLngLat(position),
    label,
    enableMeasure,
  };
  const eventContext: HasEventContext = {
    popups,
    accessEventCommand,
    isOpenedPopupByType,
    handleCreateEventClick,
  };
  const pointCUASContext: HasPointCUASContext = {
    position: toLngLat(position),
    accessCamCommand,
    movableLadCameras,
    handleLookAt,
  };
  const engagementContext: HasEngageContext = {
    isEngagementOptionHidden: !accessNeutralCommand,
    isEngagementOptionLoading: isLoadingPrepareTarget,
    isEngagementOptionDisabled: isLoadingPrepareTarget || targetEngaged || isNonActiveUserJamming,
    handleEngagement,
  };
  const disengagementContext: HasDisengageContext = {
    isDisengagementOptionHidden: !accessNeutralCommand || !targetEngaged,
    isDisengagementOptionLoading: isLoadingPrepareTarget,
    isDisengagementOptionDisabled: isLoadingPrepareTarget || isNeutralSensorJamming || isNonActiveUserJamming,
    handleDisengagement,
  };

  const targetCoreContext: TargetContextMenuContextCore = {
    accessNeutralCommand,
    ladContextIsPresent,
    canManageClearance,
    canMarkTracks,
    isAdmin,
    selectedTarget,
    targetInfo,
    droneHistoryPopup,
    healthySkyJackerSensors,
    handleOpenTargetInfo,
    handleTracking,
    handleNeutralization,
    handleLAD,
    handleOpenHistoryPopup,
    includes,
    handleUpdateOffset,
    handleShareTargetHoloview,
    handleOpenTargetControlPopup,
    handleJsonExport,
    organization,
  };

  const ctx: TargetContextMenuContext = {
    ...targetCoreContext,
    ...qrCodeContext,
    ...eventContext,
    ...measureContext,
    ...pointCUASContext,
    ...engagementContext,
    ...disengagementContext,
  };

  const options = buildContextMenuFromType<FeatureTypeToContextComponentEnum.TARGET>(
    FeatureTypeToContextComponentEnum.TARGET,
    mapId,
    ctx,
  );

  return <MenuList menuItems={options} />;
}
